package com.milta.feature.route.data.repository

import com.milta.core.cache.LocalCacheStore
import com.milta.core.network.ApiException
import com.milta.core.network.PagedResult
import com.milta.feature.route.data.remote.RouteApi
import com.milta.feature.route.data.remote.dto.RouteSummaryDto
import com.milta.feature.route.data.remote.dto.TeamRoutesOverviewDto
import com.milta.feature.route.data.remote.dto.CreateRouteBatchRequest
import com.milta.feature.route.data.remote.dto.toBatchItemDto
import com.milta.feature.route.data.remote.dto.toDomain
import com.milta.feature.route.data.remote.dto.toDto
import com.milta.feature.route.data.remote.dto.toWireValue
import com.milta.feature.route.domain.entity.AddDeliveriesResult
import com.milta.feature.route.domain.entity.DeliveryDraft
import com.milta.feature.route.domain.entity.DeliveryOutcome
import com.milta.feature.route.domain.entity.DeliveryProof
import com.milta.feature.route.domain.entity.ImportedDelivery
import com.milta.feature.route.domain.entity.Route
import com.milta.feature.route.domain.entity.RouteBatchDispatchResult
import com.milta.feature.route.domain.entity.RouteBatchResult
import com.milta.feature.route.domain.entity.RouteCreationResult
import com.milta.feature.route.domain.entity.RouteDispatchResult
import com.milta.feature.route.data.remote.dto.ReoptimizeRouteRequest
import com.milta.feature.route.domain.entity.RouteHistoryItem
import com.milta.feature.route.domain.entity.RouteOptimizationResult
import com.milta.feature.route.domain.entity.RouteProofsResult
import com.milta.feature.route.domain.entity.RouteReoptimizationResult
import com.milta.feature.route.domain.entity.RouteSummary
import com.milta.feature.route.domain.entity.TeamRoutesOverview
import com.milta.feature.route.domain.repository.RouteRepository
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

private const val ROUTE_ME_CACHE_KEY = "cache:route_me"
private const val ROUTES_DAYS_CACHE_KEY_PREFIX = "cache:route_me_days:"
private const val TEAM_OVERVIEW_CACHE_KEY_PREFIX = "cache:team_overview:"

class RouteRepositoryImpl(
    private val api: RouteApi,
    private val cacheStore: LocalCacheStore,
) : RouteRepository {

    // Mode hors ligne — meme cache-aside que getMyRoute, une entree par date (bascule Hier/Aujourd'hui
    // sur le dashboard Gestionnaire).
    override suspend fun getTeamRoutesOverview(date: String?): TeamRoutesOverview {
        val cacheKey = "$TEAM_OVERVIEW_CACHE_KEY_PREFIX${date ?: "today"}"
        return runCatching { api.getTeamRoutesOverview(date) }
            .onSuccess { cacheStore.putString(cacheKey, Json.encodeToString(it)) }
            .recoverCatching { e ->
                if (e is ApiException) throw e
                val cached = cacheStore.getString(cacheKey) ?: throw e
                Json.decodeFromString<TeamRoutesOverviewDto>(cached)
            }
            .getOrThrow()
            .toDomain()
    }

    override suspend fun getRouteById(id: String): Route =
        api.getRouteById(id).toDomain()

    // No offline cache here (unlike getTeamRoutesOverview/getMyRoute/getMyRoutes above) — MIL-52's
    // history/proofs screens have no offline requirement in the ticket, and every parameter
    // combination (period/driver/page) would need its own cache key for the fallback to ever be
    // meaningful, which isn't worth it for a read-only, non-time-critical history screen.
    override suspend fun getRouteHistory(
        from: String?,
        to: String?,
        driverId: String?,
        page: Int,
        pageSize: Int,
    ): PagedResult<RouteHistoryItem> {
        val dto = api.getRouteHistory(from, to, driverId, page, pageSize)
        return PagedResult(items = dto.items.map { it.toDomain() }, page = dto.page, pageSize = dto.pageSize, totalCount = dto.totalCount)
    }

    override suspend fun getRouteProofs(routeId: String, page: Int, pageSize: Int): RouteProofsResult =
        api.getRouteProofs(routeId, page, pageSize).toDomain()

    // Mode hors ligne — cache-aside : la derniere reponse reussie est mise en cache, et relue
    // uniquement quand le GET echoue SANS reponse HTTP du tout (pas de reseau, pas ApiException) —
    // une vraie erreur serveur (ApiException) n'utilise jamais le cache, elle remonte telle quelle.
    // Cache key scoped per [routeId] (falling back to a shared "latest" entry when null) so the
    // offline fallback never resurrects a different route than the one the driver tapped.
    override suspend fun getMyRoute(routeId: String?): RouteSummary {
        val cacheKey = "$ROUTE_ME_CACHE_KEY:${routeId ?: "latest"}"
        return runCatching { api.getMyRoute(routeId) }
            .onSuccess { cacheStore.putString(cacheKey, Json.encodeToString(it)) }
            .recoverCatching { e ->
                if (e is ApiException) throw e
                val cached = cacheStore.getString(cacheKey) ?: throw e
                Json.decodeFromString<RouteSummaryDto>(cached)
            }
            .getOrThrow()
            .toDomain()
    }

    // Mode hors ligne — meme cache-aside que getMyRoute, une entree par date interrogee (l'ecran
    // 07 Accueil Livreur navigue entre Passe/Aujourd'hui/Demain).
    override suspend fun getMyRoutes(date: String?): List<RouteSummary> {
        val cacheKey = "$ROUTES_DAYS_CACHE_KEY_PREFIX${date ?: "today"}"
        return runCatching { api.getMyRoutes(date) }
            .onSuccess { cacheStore.putString(cacheKey, Json.encodeToString(it)) }
            .recoverCatching { e ->
                if (e is ApiException) throw e
                val cached = cacheStore.getString(cacheKey) ?: throw e
                Json.decodeFromString<List<RouteSummaryDto>>(cached)
            }
            .getOrThrow()
            .map { it.toDomain() }
    }

    override suspend fun createOwnRoute(deliveries: List<DeliveryDraft>, date: String?): RouteCreationResult =
        api.createOwnRoute(deliveries.map { it.toDto() }, date).toDomain()

    override suspend fun startRoute(id: String, loadingDiscrepancyNote: String?): RouteSummary =
        api.startRoute(id, loadingDiscrepancyNote).toDomain()

    override suspend fun removeDeliveryFromRoute(routeId: String, deliveryId: String) =
        api.removeDeliveryFromRoute(routeId, deliveryId)

    override suspend fun deleteRoute(id: String) = api.deleteRoute(id)

    override suspend fun completeRoute(id: String, confirmed: Boolean): RouteSummary =
        api.completeRoute(id, confirmed).toDomain()

    override suspend fun reoptimizeInPlace(id: String): RouteSummary =
        api.reoptimizeInPlace(id).toDomain()

    // DOM-05 — forwards the driver's current GPS position (optional, more accurate depot).
    override suspend fun reoptimizeInPlace(id: String, latitude: Double?, longitude: Double?): RouteSummary =
        api.reoptimizeInPlace(id, latitude, longitude).toDomain()

    override suspend fun importRouteFile(fileBytes: ByteArray, fileName: String): List<ImportedDelivery> =
        api.importRouteFile(fileBytes, fileName).map { it.toDomain() }

    override suspend fun createRoute(driverAccountId: String, deliveries: List<DeliveryDraft>, date: String?): RouteCreationResult =
        api.createRoute(driverAccountId, deliveries.map { it.toDto() }, date).toDomain()

    override suspend fun optimizeRoute(routeId: String): RouteOptimizationResult =
        api.optimizeRoute(routeId).toDomain()

    override suspend fun regeocodeRoute(routeId: String) = api.regeocodeRoute(routeId)

    override suspend fun reoptimizeRoute(
        routeId: String,
        recipientName: String?,
        address: String,
        phoneNumber: String?,
        deliveryTimeLimit: String?,
        packageCount: Int?,
    ): RouteReoptimizationResult = api.reoptimizeRoute(
        routeId = routeId,
        request = ReoptimizeRouteRequest(
            recipientName = recipientName,
            address = address,
            phoneNumber = phoneNumber,
            deliveryTimeLimit = deliveryTimeLimit,
            packageCount = packageCount,
        ),
    ).toDomain()

    override suspend fun dispatchRoute(routeId: String, skipOptimization: Boolean?): RouteDispatchResult =
        api.dispatchRoute(routeId, skipOptimization).toDomain()

    override suspend fun addDeliveriesToRoute(routeId: String, deliveries: List<DeliveryDraft>): AddDeliveriesResult =
        api.addDeliveriesToRoute(routeId, deliveries.map { it.toDto() }).toDomain()

    override suspend fun createRouteBatch(
        driverAccountIds: List<String>,
        deliveries: List<DeliveryDraft>,
        optimize: Boolean,
        date: String?,
    ): RouteBatchResult = api.createRouteBatch(
        CreateRouteBatchRequest(
            driverAccountIds = driverAccountIds,
            deliveries = deliveries.map { it.toBatchItemDto() },
            optimize = optimize,
            date = date,
        ),
    ).toDomain()

    override suspend fun reoptimizeRouteBatch(batchId: String): RouteBatchResult =
        api.reoptimizeRouteBatch(batchId).toDomain()

    override suspend fun dispatchRouteBatch(batchId: String): RouteBatchDispatchResult =
        api.dispatchRouteBatch(batchId).toDomain()

    override suspend fun validateDelivery(
        routeId: String,
        deliveryId: String,
        photoBytes: ByteArray,
        photoFileName: String,
        status: DeliveryOutcome,
        comment: String?,
        occurredAt: String?,
    ): DeliveryProof = api.validateDelivery(
        routeId = routeId,
        deliveryId = deliveryId,
        photoBytes = photoBytes,
        photoFileName = photoFileName,
        status = status.toWireValue(),
        comment = comment,
        occurredAt = occurredAt,
    ).toDomain()
}
